import { client } from '@api/rest/client';
import type { TemplateResponseFormDTO } from '@conversed/rest/src/contracts/template-response/template-response.dto';
import ErrorCodes from '@conversed/shared/src/errors/error-codes';
import { toastErrorTranslated, toastSuccessTranslated } from '@helpers/toast';
import { mainActions } from '@store/reducers/mainHandler';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

export const useTemplateResponseActions = () => {
  const dispatch = useDispatch();

  const getTemplateResponses = useCallback(
    async (withoutLoadingAnimation = false) => {
      try {
        if (!withoutLoadingAnimation) {
          dispatch(mainActions.setUIState({ isLoading: true }));
        }
        const { data: templateResponses } =
          await client.templateResponse.getTemplateResponses();
        return templateResponses;
      } catch (error) {
        console.error(error);
        toastErrorTranslated(error);
        return [];
      } finally {
        if (!withoutLoadingAnimation) {
          dispatch(mainActions.setUIState({ isLoading: false }));
        }
      }
    },
    [dispatch],
  );

  const fetchTemplateResponses = useCallback(
    async (withoutLoadingAnimation = false) => {
      const templateResponses = await getTemplateResponses(
        withoutLoadingAnimation,
      );
      dispatch(mainActions.setTemplateResponses(templateResponses));
    },
    [getTemplateResponses, dispatch],
  );

  const createTemplateResponse = useCallback(
    async (templateResponse: TemplateResponseFormDTO) => {
      try {
        dispatch(mainActions.setUIState({ isLoading: true }));

        const response = await client.templateResponse.createTemplateResponse({
          body: templateResponse,
        });

        toastSuccessTranslated('template-responses.create.success');
        dispatch(mainActions.setTemplateResponses(response.data));
      } catch (error: any) {
        console.error(error);

        if (error?.response?.data?.message === ErrorCodes.ALREADY_EXISTS) {
          toastErrorTranslated(
            'template-responses.create.duplicate-name-toast-message',
            {
              name: templateResponse.name,
            },
          );
        } else {
          toastErrorTranslated(error);
        }

        throw error;
      } finally {
        dispatch(mainActions.setUIState({ isLoading: false }));
      }
    },
    [dispatch],
  );

  const updateTemplateResponse = useCallback(
    async (id: string, templateResponse: TemplateResponseFormDTO) => {
      try {
        dispatch(mainActions.setUIState({ isLoading: true }));

        const response = await client.templateResponse.updateTemplateResponse({
          params: { id },
          body: templateResponse,
        });

        toastSuccessTranslated('template-responses.update.success');
        dispatch(mainActions.setTemplateResponses(response.data));
      } catch (error) {
        console.error(error);
        toastErrorTranslated(error);
        throw error;
      } finally {
        dispatch(mainActions.setUIState({ isLoading: false }));
      }
    },
    [dispatch],
  );

  const deleteTemplateResponse = useCallback(
    async (id: string) => {
      try {
        dispatch(mainActions.setUIState({ isLoading: true }));

        await client.templateResponse.deleteTemplateResponse({
          params: { id },
        });

        toastSuccessTranslated('template-responses.delete.success');
        dispatch(mainActions.deleteTemplateResponse(id));
      } catch (error) {
        console.error(error);
        toastErrorTranslated(error);
      } finally {
        dispatch(mainActions.setUIState({ isLoading: false }));
      }
    },
    [dispatch],
  );

  return {
    getTemplateResponses,
    fetchTemplateResponses,
    createTemplateResponse,
    updateTemplateResponse,
    deleteTemplateResponse,
  };
};
